import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import client from '../../api/client';
import { uploadFile } from '../../utils/upload';
import {
  ArrowLeft, Save, Check, Plus, X, Upload, Image as ImageIcon, ChevronDown
} from 'lucide-react';
import { toast } from '../../components/Toast/toast';
import { useApp } from '../../context/AppContext';
import { CARE_LEVEL_OPTIONS } from '../../constants/careLevels';

const EMPTY_FORM = {
  name: '',
  latin_name: '',
  category_id: '',
  description: '',
  care: '',
  care_level: '',
  price: '',
  discount_percent: '',
  image_url: '',
  quantity: 0,
  characteristics: {},
  variants: [],
};

// Ошибки валидации FastAPI приходят массивом объектов — String() даёт
// «[object Object]» (приёмка: пользователь видел «обжект обжект»)
const humanError = (err, fallback = 'Не удалось сохранить товар') => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail.slice(0, 3).map(e => {
      const field = Array.isArray(e.loc) ? e.loc.slice(1).join('.') : '';
      if (field === 'category_id') return 'Не выбрана категория';
      if (field.startsWith('variants')) return 'Проверьте цену и остаток каждого размера';
      return e.msg ? `${field}: ${e.msg}` : 'Ошибка в данных';
    }).join('; ');
  }
  return fallback;
};

export default function ProductEditorAdmin() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(!isNew);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [specKey, setSpecKey] = useState('');
  const [specVal, setSpecVal] = useState('');
  const [imgPreview, setImgPreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const specKeyRef = useRef(null);
  const specValRef = useRef(null);
  const variantLabelRefs = useRef([]);
  const variantPriceRefs = useRef([]);
  const variantQtyRefs = useRef([]);
  const wantVariantFocus = useRef(false);
  const { user } = useApp();
  const isManager = user?.is_admin && user?.role === 'manager';

  useEffect(() => {
    client.get('/catalog/categories')
      .then(res => setCategories(res.data || []))
      .catch(() => {});
  }, []);

  // «Добавить размер» (или Enter в остатке) → фокус в новую строку
  useEffect(() => {
    if (wantVariantFocus.current) {
      wantVariantFocus.current = false;
      const last = form.variants.length - 1;
      if (last >= 0) variantLabelRefs.current[last]?.focus();
    }
  }, [form.variants.length]);

  useEffect(() => {
    if (isNew) {
      // Сразу одна строка размера: пока она одна — товар безразмерный
      setForm({ ...EMPTY_FORM, characteristics: {}, variants: [{ label: '', price: '', quantity: '0' }] });
      return;
    }
    setLoading(true);
    setLoadError('');
    client.get(`/catalog/products/${id}`)
      .then(res => {
        const p = res.data;
        const loaded = p.variants || [];
        // Строгое правило: 1 строка = безразмерный (подпись не сохраняется),
        // 2+ строки = размеры. Товар с единственным размером при открытии
        // превращается в одну строку без подписи.
        const rows = loaded.length >= 2
          ? loaded.map(v => ({
              label: v.label,
              price: String(v.price),
              quantity: String(v.quantity ?? 0),
            }))
          : loaded.length === 1
            ? [{ label: '', price: String(loaded[0].price), quantity: String(loaded[0].quantity ?? 0) }]
            : [{ label: '', price: String(p.price ?? ''), quantity: String(p.quantity ?? 0) }];
        setForm({
          name: p.name || '',
          latin_name: p.latin_name || '',
          category_id: p.category_id || '',
          description: p.description || '',
          care: p.care || '',
          care_level: p.care_level || '',
          price: p.price ?? '',
          discount_percent: p.discount_percent ?? '',
          image_url: p.image_url || '',
          quantity: p.quantity ?? 0,
          characteristics: p.characteristics || {},
          variants: rows,
        });
        setImgPreview(p.image_url || '');
      })
      .catch(() => setLoadError('Не удалось загрузить товар'))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (field === 'image_url') setImgPreview(value);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const url = await uploadFile(file);
      handleChange('image_url', url);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : JSON.stringify(detail) || err.message || 'Неизвестная ошибка';
      setError(`Ошибка загрузки фото: ${msg}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const addSpec = () => {
    if (!specKey.trim()) return;
    setForm(prev => ({ ...prev, characteristics: { ...prev.characteristics, [specKey.trim()]: specVal } }));
    setSpecKey('');
    setSpecVal('');
  };

  const removeSpec = (key) => {
    setForm(prev => {
      const s = { ...prev.characteristics };
      delete s[key];
      return { ...prev, characteristics: s };
    });
  };

  const addVariant = () => {
    setForm(prev => ({ ...prev, variants: [...prev.variants, { label: '', price: '', quantity: '0' }] }));
  };

  // Добавить размер и сразу ввести в его строку
  const handleAddVariant = () => {
    wantVariantFocus.current = true;
    addVariant();
  };

  const changeVariant = (idx, field, value) => {
    setForm(prev => ({
      ...prev,
      variants: prev.variants.map((v, i) => (i === idx ? { ...v, [field]: value } : v)),
    }));
  };

  const removeVariant = (idx) => {
    setForm(prev => ({ ...prev, variants: prev.variants.filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    if (!form.category_id) {
      toast.error('Сначала выберите категорию');
      return;
    }

    if (form.variants.length === 0) {
      toast.error('Укажите цену: заполните строку размера');
      return;
    }
    // Строго: одна строка = безразмерный товар, две и более = размеры
    if (form.variants.length === 1) {
      const r = form.variants[0];
      if (!(Number(r.price) > 0)
        || r.quantity.trim() === ''
        || !(Number.isInteger(Number(r.quantity)) && Number(r.quantity) >= 0)
      ) {
        toast.error('Заполните цену (> 0) и остаток (целое ≥ 0)');
        return;
      }
    } else {
      const bad = form.variants.some(v =>
        !v.label.trim()
        || !(Number(v.price) > 0)
        || v.quantity.trim() === ''
        || !(Number.isInteger(Number(v.quantity)) && Number(v.quantity) >= 0)
      );
      if (bad) {
        toast.error('Заполните подпись, цену (> 0) и остаток (целое ≥ 0) у каждого размера');
        return;
      }
    }

    // Автоматически сохранить последнюю незаписанную пару ключ-значение
    const finalForm = specKey.trim()
      ? { ...form, characteristics: { ...form.characteristics, [specKey.trim()]: specVal } }
      : form;

    // 1 строка → безразмерный (variants=[]); 2+ → размеры,
    // общая цена = минимальная, остаток = сумма (сервер тоже пересчитывает)
    const rows = finalForm.variants;
    const sizeless = rows.length <= 1;

    setSaving(true);
    setSaved(false);
    try {
      const payload = {
        name: finalForm.name,
        latin_name: finalForm.latin_name.trim() || null,
        category_id: finalForm.category_id,
        description: finalForm.description || '',
        care: finalForm.care || '',
        care_level: finalForm.care_level || null,
        price: sizeless
          ? Number(rows[0].price) || 0
          : Math.min(...rows.map(r => Number(r.price) || 0)),
        discount_percent: finalForm.discount_percent ? Number(finalForm.discount_percent) : null,
        image_url: finalForm.image_url || '',
        quantity: sizeless
          ? Number(rows[0].quantity) || 0
          : rows.reduce((sum, r) => sum + (Number(r.quantity) || 0), 0),
        characteristics: finalForm.characteristics,
        variants: sizeless
          ? []
          : rows.map(v => ({
              label: v.label.trim(),
              price: Number(v.price),
              quantity: Number(v.quantity),
            })),
      };
      if (isNew) {
        await client.post('/admin/products', payload);
      } else {
        await client.put(`/admin/products/${id}`, payload);
      }
      setSaved(true);
      setTimeout(() => navigate('/admin/products'), 800);
    } catch (err) {
      toast.error(humanError(err));
    } finally {
      setSaving(false);
    }
  };

  // Спека S5: латынь и строки размеров редактирует только owner/developer;
  // менеджер по прямому URL не должен попадать в редактор
  if (isManager) {
    return <Navigate to="/admin/products" replace />;
  }

  if (loadError) {
    return (
      <div className="space-y-4">
        <div className="rounded-xl border border-[#D64545]/30 bg-[#D64545]/10 p-4 text-sm text-[#D64545]">
          {loadError}
        </div>
        <button
          onClick={() => navigate('/admin/products')}
          className="flex items-center gap-1.5 text-sm font-semibold text-[#374151] hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          К списку товаров
        </button>
      </div>
    );
  }

  // Строго: 1 строка = безразмерный, 2+ = размеры
  const hasVariants = form.variants.length >= 2;

  return (
    <div className="space-y-4">
      {/* Шапка редактора: «Назад» и «Сохранить» всегда на виду — как карточки ниже */}
      <div className="sticky top-[52px] z-20 flex items-center gap-2 rounded-2xl border border-[#e2e8f0] bg-white px-4 py-3">
        <button
          onClick={() => navigate('/admin/products')}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[#f1f5f9] text-[#64748b] transition active:scale-90"
          aria-label="Назад к списку"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <h2 className="min-w-0 flex-1 truncate text-sm font-bold text-[#0f172a]">
          {isNew ? 'Новый товар' : 'Редактирование'}
        </h2>
        <button
          onClick={handleSave}
          disabled={saving || saved || !form.name.trim()}
          className={`flex shrink-0 items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold shadow-sm transition active:scale-[0.97] disabled:opacity-50 ${
            saved
              ? 'bg-[#6B9B74]/15 text-[#6B9B74]'
              : 'bg-[#374151] text-white hover:bg-[#1f2937]'
          }`}
        >
          {saved ? <Check className="h-4 w-4" /> : <Save className="h-4 w-4" />}
          {saved ? 'Сохранено!' : saving ? 'Сохранение...' : 'Сохранить'}
        </button>
      </div>

      {loading ? (
        <div className="rounded-2xl bg-[#f8fafc] py-16 text-center text-sm text-[#64748b]">
          Загрузка...
        </div>
      ) : (
        <>
          {error && (
            <div className="rounded-lg bg-[#D64545]/10 p-3 text-sm text-[#D64545]">{error}</div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Левая колонка — основное */}
            <div className="space-y-4 rounded-2xl border border-[#e2e8f0] bg-white p-4">
              <h3 className="text-sm font-bold text-[#0f172a]">Основное</h3>

              {/* Фото */}
              <div>
                <label className="mb-1 block text-xs font-medium text-[#64748b]">Фото</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <div className="flex items-center gap-3">
                  {imgPreview ? (
                    <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-[#e2e8f0]">
                      <img src={imgPreview} alt="preview" className="h-full w-full object-cover" onError={() => setImgPreview('')} />
                    </div>
                  ) : (
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg border-2 border-dashed border-[#e2e8f0] text-[#e2e8f0]">
                      <ImageIcon className="h-6 w-6" />
                    </div>
                  )}
                  <div className="flex flex-col gap-1.5">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="flex items-center gap-1.5 rounded-lg border border-[#e2e8f0] px-3 py-2 text-xs font-medium text-[#64748b] transition hover:bg-[#f8fafc] active:scale-95 disabled:opacity-50"
                    >
                      <Upload className="h-3.5 w-3.5" />
                      {uploading ? 'Загрузка...' : 'Выбрать фото'}
                    </button>
                    {imgPreview && (
                      <button
                        type="button"
                        onClick={() => { handleChange('image_url', ''); setImgPreview(''); }}
                        className="text-xs text-[#D64545]/70 hover:text-[#D64545]"
                      >
                        Удалить фото
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Название */}
              <div>
                <label className="mb-1 block text-xs font-medium text-[#64748b]">Название *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => handleChange('name', e.target.value)}
                  placeholder="Название товара"
                  className="w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2 text-sm outline-none focus:border-[#6B9B74] focus:bg-white"
                />
              </div>

              {/* Латинское название */}
              <div>
                <label className="mb-1 block text-xs font-medium text-[#64748b]">Латинское название</label>
                <input
                  type="text"
                  value={form.latin_name}
                  onChange={e => handleChange('latin_name', e.target.value)}
                  placeholder="Malus domestica (необязательно)"
                  className="w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2 text-sm outline-none focus:border-[#6B9B74] focus:bg-white"
                />
              </div>

              {/* Категория */}
              <div>
                <label className="mb-1 block text-xs font-medium text-[#64748b]">Категория *</label>
                <div className="relative">
                  <select
                    value={form.category_id}
                    onChange={e => handleChange('category_id', e.target.value)}
                    className="w-full appearance-none rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2 text-sm outline-none focus:border-[#6B9B74] focus:bg-white"
                  >
                    <option value="">Выберите категорию</option>
                    {categories.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#64748b]" />
                </div>
              </div>

              {/* Характеристики */}
              <div>
                <label className="mb-1 block text-xs font-medium text-[#64748b]">Характеристики</label>
                {Object.entries(form.characteristics).length > 0 && (
                  <div className="mb-2 space-y-1">
                    {Object.entries(form.characteristics).map(([k, v]) => (
                      <div key={k} className="flex items-center gap-2 rounded-lg bg-[#f8fafc] px-3 py-1.5 text-sm">
                        <span className="font-medium text-[#0f172a]">{k}:</span>
                        <span className="flex-1 text-[#64748b]">{v}</span>
                        <button onClick={() => removeSpec(k)} className="text-[#64748b] hover:text-[#D64545]">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <input
                    ref={specKeyRef}
                    type="text"
                    placeholder="Ключ"
                    list="spec-key-suggestions"
                    value={specKey}
                    onChange={e => setSpecKey(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        specValRef.current?.focus();
                      }
                    }}
                    className="w-1/3 rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2 text-sm outline-none focus:border-[#6B9B74] focus:bg-white"
                  />
                  <datalist id="spec-key-suggestions">
                    <option value="Высота" />
                    <option value="Ширина" />
                    <option value="Возраст" />
                    <option value="Освещение" />
                    <option value="Полив" />
                    <option value="Зимостойкость" />
                    <option value="Место" />
                    <option value="Тип" />
                  </datalist>
                  <input
                    ref={specValRef}
                    type="text"
                    placeholder="Значение"
                    value={specVal}
                    onChange={e => setSpecVal(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addSpec();
                        specKeyRef.current?.focus();
                      }
                    }}
                    className="flex-1 rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2 text-sm outline-none focus:border-[#6B9B74] focus:bg-white"
                  />
                  <button
                    onClick={addSpec}
                    className="shrink-0 rounded-lg border border-[#e2e8f0] px-3 py-2 text-sm text-[#64748b] hover:bg-[#f8fafc] active:scale-95"
                    aria-label="Добавить характеристику"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                <p className="mt-1 text-[11px] text-[#94a3b8]">
                  Enter в «Значение» — добавить и вернуться к новому ключу; в «Ключ» — перейти к значению.
                </p>
              </div>
            </div>

            {/* Правая колонка — цены и остатки */}
            <div className="space-y-4 rounded-2xl border border-[#e2e8f0] bg-white p-4">
              <h3 className="text-sm font-bold text-[#0f172a]">Цены и остатки</h3>

              {/* Скидка — шаблоны процентов вместо старой цены */}
              <div>
                <label className="mb-1 block text-xs font-medium text-[#64748b]">Скидка</label>
                <div className="relative">
                  <select
                    value={form.discount_percent}
                    onChange={e => handleChange('discount_percent', e.target.value)}
                    className="w-full appearance-none rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2 text-sm outline-none focus:border-[#6B9B74] focus:bg-white"
                  >
                    <option value="">Без скидки</option>
                    <option value="5">−5%</option>
                    <option value="10">−10%</option>
                    <option value="15">−15%</option>
                    <option value="20">−20%</option>
                    <option value="25">−25%</option>
                    <option value="50">−50% (Распродажа)</option>
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#64748b]" />
                </div>
                <p className="mt-1 text-[11px] text-[#94a3b8]">
                  Цена — в строках размеров ниже. Скидка рисует на витрине
                  зачёркнутую «цену до» и значок −%. При 50% добавляется плашка «Распродажа».
                </p>
              </div>

              {/* Размеры — каждый отдельной строкой, в столбик */}
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <label className="block text-xs font-medium text-[#64748b]">Размеры</label>
                  <button
                    type="button"
                    onClick={handleAddVariant}
                    aria-label="Добавить размер"
                    className="grid h-7 w-7 place-items-center rounded-lg border border-[#e2e8f0] text-[#64748b] transition hover:bg-[#f8fafc] active:scale-95"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                <p className="mb-2 text-[11px] text-[#94a3b8]">
                  Одна строка — товар без размеров: цена и остаток берутся из неё,
                  подпись не нужна. Кнопка + или Enter добавляет строку; с двумя
                  строками и более товар начинает продаваться по размерам.
                </p>
                <div className="space-y-2">
                  {form.variants.map((v, idx) => (
                    <div key={idx} className="flex items-center gap-1.5">
                      <input
                        ref={el => { variantLabelRefs.current[idx] = el; }}
                        type="text"
                        value={v.label}
                        onChange={e => changeVariant(idx, 'label', e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            variantPriceRefs.current[idx]?.focus();
                          }
                        }}
                        placeholder="5 л"
                        className="w-28 shrink-0 rounded-lg border border-[#e2e8f0] bg-white px-2.5 py-2 text-sm outline-none focus:border-[#6B9B74]"
                      />
                      <input
                        ref={el => { variantPriceRefs.current[idx] = el; }}
                        type="number"
                        min="0"
                        value={v.price}
                        onChange={e => changeVariant(idx, 'price', e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            variantQtyRefs.current[idx]?.focus();
                          }
                        }}
                        placeholder="Цена ₽"
                        className="min-w-0 flex-1 rounded-lg border border-[#e2e8f0] bg-white px-2.5 py-2 text-sm outline-none focus:border-[#6B9B74]"
                      />
                      <input
                        ref={el => { variantQtyRefs.current[idx] = el; }}
                        type="number"
                        min="0"
                        value={v.quantity}
                        onChange={e => changeVariant(idx, 'quantity', e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddVariant();
                          }
                        }}
                        placeholder="Шт"
                        className="w-[3.5rem] shrink-0 rounded-lg border border-[#e2e8f0] bg-white px-2 py-2 text-sm outline-none focus:border-[#6B9B74]"
                      />
                      <button
                        type="button"
                        onClick={() => removeVariant(idx)}
                        disabled={form.variants.length <= 1}
                        className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-[#94a3b8] transition hover:text-[#D64545] disabled:opacity-30 disabled:hover:text-[#94a3b8]"
                        aria-label="Удалить размер"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
                {hasVariants && (
                  <p className="mt-1.5 text-[11px] text-[#94a3b8]">
                    Товар с размерами: общая цена — минимальная из строк,
                    общий остаток — сумма по строкам.
                  </p>
                )}
              </div>
            </div>

            {/* Описание и уход — отдельная секция (тексты не в ценах) */}
            <div className="space-y-4 rounded-2xl border border-[#e2e8f0] bg-white p-4 sm:col-span-2">
              <h3 className="text-sm font-bold text-[#0f172a]">Описание и уход</h3>

              <div>
                <label className="mb-1 block text-xs font-medium text-[#64748b]">Описание</label>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={e => handleChange('description', e.target.value)}
                  placeholder="Описание товара"
                  className="w-full resize-none rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2 text-sm outline-none focus:border-[#6B9B74] focus:bg-white"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-[#64748b]">Уровень ухода</label>
                <div className="relative">
                  <select
                    value={form.care_level}
                    onChange={e => handleChange('care_level', e.target.value)}
                    className="w-full appearance-none rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2 text-sm outline-none focus:border-[#6B9B74] focus:bg-white"
                  >
                    <option value="">Не указан</option>
                    {CARE_LEVEL_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#64748b]" />
                </div>
                <p className="mt-1 text-[11px] text-[#94a3b8]">
                  Цветной бейдж на карточке и фильтр «Уход» в каталоге:
                  Лёгкий / Средний / Требовательный.
                </p>
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-[#64748b]">Рекомендации по уходу</label>
                <textarea
                  rows={3}
                  value={form.care}
                  onChange={e => handleChange('care', e.target.value)}
                  placeholder="Полив, подкормка, укрытие на зиму"
                  className="w-full resize-none rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2 text-sm outline-none focus:border-[#6B9B74] focus:bg-white"
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
